import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { articlesAPI } from '../services/api';
import Header from '../components/Header';
import './Dashboard.css';

const Dashboard = () => {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, published
  const navigate = useNavigate();

  useEffect(() => {
    loadArticles();
    loadStats();
  }, []);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const response = await articlesAPI.getAll();
      setArticles(response.data.articles);
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await articlesAPI.getStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const getPlatformStatus = (article, platform) => {
    return article.platforms?.[platform]?.status || 'pending';
  };

  const getPlatformUrl = (article, platform) => {
    return article.platforms?.[platform]?.url;
  };

  const filterArticles = () => {
    if (filter === 'all') return articles;

    return articles.filter((article) => {
      const platforms = ['substack', 'medium', 'linkedin'];
      if (filter === 'pending') {
        return platforms.some((p) => getPlatformStatus(article, p) === 'pending');
      } else if (filter === 'published') {
        return platforms.every((p) => getPlatformStatus(article, p) === 'published');
      }
      return true;
    });
  };

  const filteredArticles = filterArticles();

  return (
    <div className="dashboard">
      <Header />

      <div className="container dashboard-content">
        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Total Articles</div>
              <div className="stat-value">{stats.total}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Substack</div>
              <div className="stat-value">
                {stats.platforms.substack.published}
                <span className="stat-secondary">/ {stats.total}</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Medium</div>
              <div className="stat-value">
                {stats.platforms.medium.published}
                <span className="stat-secondary">/ {stats.total}</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">LinkedIn</div>
              <div className="stat-value">
                {stats.platforms.linkedin.published}
                <span className="stat-secondary">/ {stats.total}</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="actions-bar">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({articles.length})
            </button>
            <button
              className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              Pending
            </button>
            <button
              className={`filter-tab ${filter === 'published' ? 'active' : ''}`}
              onClick={() => setFilter('published')}
            >
              Published
            </button>
          </div>

          <button className="btn btn-primary" onClick={() => navigate('/articles/new')}>
            + New Article
          </button>
        </div>

        {/* Articles List */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading articles...</p>
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="empty-state">
            <h3>No articles found</h3>
            <p>Create your first article to get started</p>
            <button className="btn btn-primary" onClick={() => navigate('/articles/new')}>
              Create Article
            </button>
          </div>
        ) : (
          <div className="articles-grid">
            {filteredArticles.map((article) => (
              <div key={article.article_id} className="article-card">
                <div className="article-header">
                  <h3 className="article-title">{article.article_title}</h3>
                  <div className="article-meta">
                    <span className="article-id">{article.article_id}</span>
                  </div>
                </div>

                <div className="article-platforms">
                  <div className="platform-status">
                    <span className="platform-name">Substack</span>
                    <span
                      className={`badge ${
                        getPlatformStatus(article, 'substack') === 'published'
                          ? 'badge-success'
                          : 'badge-pending'
                      }`}
                    >
                      {getPlatformStatus(article, 'substack')}
                    </span>
                    {getPlatformUrl(article, 'substack') && (
                      <a
                        href={getPlatformUrl(article, 'substack')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="platform-link"
                      >
                        View
                      </a>
                    )}
                  </div>

                  <div className="platform-status">
                    <span className="platform-name">Medium</span>
                    <span
                      className={`badge ${
                        getPlatformStatus(article, 'medium') === 'published'
                          ? 'badge-success'
                          : 'badge-pending'
                      }`}
                    >
                      {getPlatformStatus(article, 'medium')}
                    </span>
                    {getPlatformUrl(article, 'medium') && (
                      <a
                        href={getPlatformUrl(article, 'medium')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="platform-link"
                      >
                        View
                      </a>
                    )}
                  </div>

                  <div className="platform-status">
                    <span className="platform-name">LinkedIn</span>
                    <span
                      className={`badge ${
                        getPlatformStatus(article, 'linkedin') === 'published'
                          ? 'badge-success'
                          : 'badge-pending'
                      }`}
                    >
                      {getPlatformStatus(article, 'linkedin')}
                    </span>
                    {getPlatformUrl(article, 'linkedin') && (
                      <a
                        href={getPlatformUrl(article, 'linkedin')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="platform-link"
                      >
                        View
                      </a>
                    )}
                  </div>
                </div>

                <div className="article-footer">
                  <div className="article-tags">
                    {article.tags?.slice(0, 3).map((tag, idx) => (
                      <span key={idx} className="tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="article-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(`/articles/${article.article_id}`)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(`/articles/${article.article_id}/publish`)}
                    >
                      Publish
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
